import { supabase } from '@/lib/supabase'
import type { ApiError, Recipe } from '@/types'

/**
 * Recipe Service
 * Provides methods to discover and search recipes in the global pool.
 */
function createRecipeService() {
  /**
   * Search the global recipes pool.
   * @param query Keyword string (empty for recently extracted first page)
   * @returns Array of Recipe stubs (selected flags default to false)
   */
  const search = async (query: string): Promise<Recipe[]> => {
    const { data, error } = await supabase.rpc('search_recipes', {
      search_query: query,
    })

    if (error) {
      const apiError: ApiError = {
        message: error.message,
        status: 500,
      }
      throw apiError
    }

    // Map DB rows to partial Recipe objects.
    // Global recipes don't carry mix-specific fields (selected, sharer, etc.).
    return (data || []).map((row: Record<string, unknown>) => ({
      id: row.id as string,
      author: (row.author as string | null) ?? undefined,
      name: row.name as string,
      url: row.url as string,
      instructions: (row.instructions as string | null) ?? undefined,
      image: (row.image as string | null) ?? undefined,
      ingredientIds: (row.ingredient_ids as string[] | null) ?? [],
      favoriteCount: (row.favorite_count as number | null) ?? 0,
      selected: false,
      selectedBy: null,
      selectedByName: null,
      sharerName: undefined,
      createdAt: (row.created_at as string) ?? new Date().toISOString(),
      updatedAt: (row.created_at as string) ?? new Date().toISOString(),
    }))
  }

  return {
    search,
  }
}

export const recipeService = createRecipeService()
